import { useState, useEffect } from 'react';
import { BlogPost, BrowserXMLParser } from '../utils/browserXMLParser';

interface UseBlogXMLResult {
  posts: BlogPost[];
  loading: boolean;
  error: string | null;
  totalPosts: number;
}

export const useBlogXML = (): UseBlogXMLResult => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadBlogXML = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load the XML file from the public folder
        const response = await fetch('/ClientDocs/awasiblog.WordPress.2025-07-22.xml');
        if (!response.ok) {
          throw new Error(`Failed to load XML: ${response.statusText}`);
        }

        const xmlContent = await response.text();
        const parsedPosts = await BrowserXMLParser.parseWordPressXML(xmlContent);
        
        setPosts(parsedPosts);
      } catch (err) {
        console.error('Error loading blog XML:', err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      } finally {
        setLoading(false);
      }
    };

    loadBlogXML();
  }, []);

  return {
    posts,
    loading,
    error,
    totalPosts: posts.length
  };
};

// Hook for paginated blog posts
export const usePaginatedBlogXML = (page: number = 1, postsPerPage: number = 5) => {
  const { posts, loading, error, totalPosts } = useBlogXML();
  
  const startIndex = (page - 1) * postsPerPage;
  const endIndex = startIndex + postsPerPage;
  const paginatedPosts = posts.slice(startIndex, endIndex);
  
  const totalPages = Math.ceil(totalPosts / postsPerPage);
  const hasNextPage = page < totalPages;
  const hasPreviousPage = page > 1;

  return {
    posts: paginatedPosts,
    loading,
    error,
    totalPosts,
    totalPages,
    currentPage: page,
    hasNextPage,
    hasPreviousPage,
    postsPerPage
  };
};